const AI_FEATURE_OTHER_KEY = "other";
const CATALOG_ENTRIES = [
  // ── seat: included with Agent seats ────────────────────────────────────────
  {
    key: "ingestion_transcription",
    label: "Ingestion & transcription",
    packaging: "seat",
    // LLM slice only: ASR/transcription audio minutes (the dominant cost) are
    // not LLM generations and never appear in Langfuse.
    observationNames: [
      "identify_recording_speakers",
      "identify_recording_debrief_speakers"
    ]
  },
  {
    key: "call_grading",
    label: "Call grading",
    packaging: "seat",
    // Re-grading reuses these exact generation names, so its cost lands here.
    // Both are seat-included, so the merge is accurate rather than a smear.
    observationNames: [
      "generate_recording_assessment_section_relevance",
      "generate_recording_assessment_criteria",
      "grading_outcome_extraction",
      "validate_recording_grade",
      "generate_templated_customer_interaction_summary",
      "generate_recording_summary",
      "generate_recording_debrief_summary",
      "generate_recording_title",
      "generate_recording_short_summary",
      "generate_recording_email_suggestion",
      "find_recording_email_template_suggestion",
      "playbook_routing",
      "extract_coaching_observation",
      "grading_data_extraction",
      "generate_criteria_coverage_durations"
    ],
    observationNamePrefixes: ["month-in-review-"],
    traceNames: [
      "teammate_account_assessment",
      "recording_assessment_section_relevance",
      "recording_assessment_criteria_grading",
      "recording_assessment_criteria_coverage",
      "recording_assessment_outcome_extraction",
      // OTel HTTP root spans for in-request grading flows.
      "POST /performance/gradings/:gradingId/validate-grade-stream",
      "POST /performance/gradings/:gradingId/grade-highlighting-stream"
    ]
  },
  {
    key: "grading_reassessment",
    label: "Re-grading",
    packaging: "seat"
    // Deliberately unmatchable: re-grading reuses call_grading's generation
    // names verbatim, so its Langfuse rows attribute there — which is also
    // where its cost belongs now that re-grading is seat-included rather than
    // credit-billed, so there is nothing left to separate out.
  },
  {
    key: "configuration_assistants",
    label: "Configuration assistants",
    packaging: "seat",
    // Simulation/test tooling is grouped here as part of the configuration
    // workflow — [Q: confirm with Kevin].
    observationNames: [
      "config_agent_simulation",
      "playbook_test_simulation_agent",
      "teammate_agent_simulation",
      "generate_test_user_message",
      "generate_test_case_customer_turn",
      "generate_test_case_assertion_verdicts",
      "generate_teammate_agent_system_prompt",
      "teammate_account_call_outline",
      "knowledge_base_document_metadata_generation",
      "simulate-conversation",
      "public-api-simulate-conversation"
    ],
    // History only: of these, only the simulation agent still sets a trace
    // name. The rest ran as standalone agents before their surfaces were
    // unified into Ask Alpharun, and the names stay so their recorded traces
    // keep attributing here.
    traceNames: [
      "Simulation Agent",
      // Retired agents, kept so their historical traces stay attributed here.
      // Delete after 2027-09.
      "Configuration Agent",
      "Playbook configuration agent",
      "Signal configuration agent",
      "Knowledge Base Agent",
      // OTel HTTP root span of a "Try this agent" turn.
      "POST /agents/:teammateAgentId/simulation"
    ]
  },
  {
    key: "standard_mcp_api",
    label: "Standard MCP / API",
    packaging: "seat",
    // Trace-name prefix from buildMcpToolTraceName: runMcpTaskJob roots one
    // trace per execution of one of our own MCP tools (`mcp:<tool_name>`, e.g.
    // `mcp:test-criterion-proposal`) so the LLM fan-out that tool triggers
    // rolls up under it. The pricing doc splits standard (seat) vs
    // compute-heavy (credits) MCP per tool; until that tool list exists, all
    // MCP traffic lands here.
    traceNamePrefixes: ["mcp:"]
  },
  {
    key: "coaching_notes",
    label: "Coaching notes",
    packaging: "seat",
    // Keep the retired split trace name so historical usage remains attributed
    // to coaching notes alongside current weekly and follow-up runs.
    traceNames: [
      "playbook_coaching",
      "playbook_coaching_split",
      "playbook_coaching_opportunities"
    ],
    // History only: before playbookCoachingAgent.ts set a trace name its runs
    // arrived as traceless ai-sdk `chat <model>` worker spans, and it is the
    // only Anthropic and gpt-5.5 caller in the codebase, so those attribute
    // here by model family. Older gpt-5.6-sol runs stay indistinguishable from
    // automations and fall into scheduled_agents.
    fallbackObservationNamePrefixes: ["chat claude-", "chat gpt-5.5-"]
  },
  {
    key: "deep_search",
    label: "Deep search",
    packaging: "seat",
    // The LLM slice of transcript search (turbopuffer/transcriptQueryExpansion);
    // embeddings are untraced today.
    observationNames: ["transcript_search_query_expansion"]
  },
  // ── plan_gated: plan eligibility, no credits for standard use ──────────────
  {
    key: "vocal_intelligence",
    label: "Vocal intelligence",
    packaging: "plan_gated",
    observationNamePrefixes: ["vocal-intelligence-"]
  },
  {
    key: "live_coaching",
    label: "Live coaching",
    packaging: "plan_gated",
    observationNames: ["live_coaching_copilot"]
  },
  {
    key: "kb_adherence",
    label: "KB adherence",
    packaging: "plan_gated",
    observationNames: ["kb_adherence_check"],
    traceNames: ["knowledge_base_adherence_extraction"]
  },
  {
    key: "cx_score",
    label: "CX score",
    packaging: "plan_gated",
    observationNames: ["customer_experience_scoring"]
  },
  // ── credits: shared AI credits ─────────────────────────────────────────────
  {
    key: "research_agent",
    label: "Ask Alpharun",
    packaging: "credits",
    // Research-agent findings (researchAgentHelpers.ts) plus the in-app chat
    // assistant (chatHelpers.ts) and grading copilot (teammateCopilotHelpers.ts):
    // chat Q&A counts as Research Agent-style analysis → credits. The findings
    // generations bill ambient-first, so the same work run inside an automation
    // or MCP task bills that surface instead.
    observationNames: [
      "teammate_account_grading_finding",
      "teammate_performance_finding",
      "chat_assistant",
      "chat_assistant_tool_search_recordings",
      "teammate_copilot"
    ],
    traceNames: [
      "Research Agent",
      // OTel HTTP root spans for in-request copilot/chat flows.
      "POST /teammates/:teammateId/copilot",
      "POST /chat/assistant"
    ],
    // Every trace here roots exactly one Q&A turn: a research-agent turn, a
    // chat-assistant request, or a copilot request.
    featureUnit: {
      noun: { singular: "question", plural: "questions" },
      traceNames: [
        "Research Agent",
        "POST /teammates/:teammateId/copilot",
        "POST /chat/assistant"
      ],
      // The HTTP-route trace names exist since the Sentry→Langfuse span
      // processor shipped (fa6d20916); the Research Agent trace is older.
      countedSinceIso: "2026-01-25"
    }
  },
  {
    key: "scheduled_agents",
    label: "Automations",
    packaging: "credits",
    traceNames: [
      "automation_orchestration",
      "automation_report_subagent",
      // The reading of a prompt-defined agent's instructions, which runs before
      // the orchestration it configures and so roots its own trace.
      "automation_interpretation"
    ],
    // One orchestration trace per run; the report subagent's traces belong to
    // the same run, so counting them too would double-count.
    featureUnit: {
      noun: { singular: "run", plural: "runs" },
      traceNames: ["automation_orchestration"],
      // The orchestrator names its traces since the AI cost dashboard PR
      // (cb00d9c5c); earlier runs were traceless and are not attributed here.
      countedSinceIso: "2026-08-05"
    }
  },
  {
    key: "signals",
    label: "Signals",
    packaging: "credits",
    observationNames: ["signal_match_extraction"],
    traceNames: ["POST /signals/test-stream"],
    // Matches never root their own trace — each is one
    // signal_match_extraction generation inside the recording-processing
    // trace — so the unit is counted by observation name instead.
    featureUnit: {
      noun: { singular: "match", plural: "matches" },
      observationNames: ["signal_match_extraction"],
      // The generation name exists since the signals backend (0a5ad6794).
      countedSinceIso: "2026-03-27"
    }
  },
  {
    key: "mcp_tool_call",
    label: "AI MCP tools",
    packaging: "credits",
    // The MCP tools that use LLM backed tool calls. Exact trace names win over standard_mcp_api's `mcp:` prefix.
    // Keep in sync with the `billsAgainstCredits` task jobs (runMcpTaskJob.ts).
    // criteria-coverage-report-run is criteria-time-breakdown-run's pre-rename
    // trace name, kept so history stays attributed.
    traceNames: [
      "mcp:test-criterion-proposal",
      "mcp:sample-criterion-score-distribution",
      "mcp:sample-section-applicability-distribution",
      "mcp:criteria-time-breakdown-run",
      "mcp:criteria-coverage-report-run",
      "mcp:research-customer-interactions-run",
      "mcp:research-teammates-run"
    ],
    // runMcpTaskJob roots one trace per tool execution, so each trace is one
    // tool call.
    featureUnit: {
      noun: { singular: "tool call", plural: "tool calls" },
      traceNames: [
        "mcp:test-criterion-proposal",
        "mcp:sample-criterion-score-distribution",
        "mcp:sample-section-applicability-distribution",
        "mcp:criteria-time-breakdown-run",
        "mcp:criteria-coverage-report-run",
        "mcp:research-customer-interactions-run",
        "mcp:research-teammates-run"
      ],
      // `mcp:` trace names exist since MCP tool tracing shipped (df1a45c0d).
      countedSinceIso: "2026-07-06"
    }
  },
  // ── addon: separate SKUs and meters ────────────────────────────────────────
  {
    key: "topics",
    label: "Topics",
    packaging: "addon",
    // The interaction-moment extraction pass behind topics
    // (interactionMomentExtraction/).
    observationNames: ["sentiment_and_coaching_extraction", "facet_extraction"]
  },
  {
    key: "training_role_play",
    label: "Training / Role-play",
    packaging: "addon",
    observationNames: [
      "role_play_assessment",
      "standard_assessment_criteria",
      "data_extraction_field"
    ],
    observationNamePrefixes: [
      "training_",
      "course_",
      "learning_journey_role_play_realism_",
      "knowledge_test_"
    ],
    traceNames: ["POST /interview/course/lessons/:lessonId/complete-chat"]
  },
  {
    key: "voice_agents",
    label: "Voice agents",
    packaging: "addon",
    observationNames: [
      "voice_agent_web_document_research",
      "call_task_agent_turn"
    ],
    // The LiveKit recording agent (livekit.ts `buildLivekitAgentName`): a
    // conversational agent on live calls. Traces are `recording-<version>-<ns>`
    // and include zero-token pipeline spans (tts_node, user_turn) alongside
    // llm_node/agent_turn. Non-LLM cost (TTS minutes etc.) is not in Langfuse.
    traceNamePrefixes: ["recording-1py-"]
  },
  // ── internal: Alpharun's own spend ─────────────────────────────────────────
  {
    key: "demo_data",
    label: "Demo data generation",
    packaging: "internal",
    observationNamePrefixes: ["generate_demo_"]
  },
  {
    key: "hq_tooling",
    label: "HQ tooling & playgrounds",
    packaging: "internal",
    // OTel HTTP root spans: every LLM call rooted in an internal-tooling
    // request (coaching-note playground, automation dry-runs, …).
    traceNamePrefixes: ["POST /tooling/"]
  },
  {
    key: "evals_scripts",
    label: "Evals & scripts",
    packaging: "internal",
    // The throwaway names come from the instrumentation smoke-test scripts
    // that emit them (testScriptInstrumentation.ts,
    // testServerlessInstrumentation.ts, apps/api/scripts/testInstrumentation/);
    // the rest are one-off eval runs.
    observationNames: [
      "test_script",
      "greeting",
      "farewell",
      "respond_to_prompt",
      "respond",
      "hello",
      "grading_improvement_analysis",
      "score-customer-experience",
      "charlie_health_group_facilitator_grading_recommendations",
      "playbook_routing_eval",
      "standard_assessment_criterion_eval",
      "interview_summary_eval"
    ]
  },
  {
    key: "call_analysis",
    label: "Call analysis",
    packaging: "internal",
    // One trace per step over a company's recorded calls, whichever step
    // (packages/server-helpers/src/callAnalysis/trace.ts).
    traceNamePrefixes: ["call_analysis:"]
  },
  // ── excluded: recruiting product line ──────────────────────────────────────
  {
    key: "recruiting_interviews",
    label: "Recruiting interviews",
    packaging: "excluded",
    observationNames: [
      "interview_grading",
      "interview_completion_status",
      "recruiting_assessment_generation",
      "recruiting_question_generation",
      "job_questions_feedback"
    ]
  },
  {
    key: "scheduling",
    label: "Candidate scheduling",
    packaging: "excluded",
    observationNamePrefixes: ["candidate_availability_"]
  },
  {
    key: "cefr_assessment",
    label: "CEFR assessment",
    packaging: "excluded",
    // Both spellings exist in history (hyphen/underscore mismatch in
    // cefrLanguageAssessment.ts); history is immutable, so keep both.
    observationNames: ["cefr_assessment", "cefr-assessment"]
  },
  // ── tbd: packaging undecided — [Q] items surfaced as "Unassigned" ──────────
  {
    key: "tool_usage_accuracy",
    label: "Tool-usage accuracy",
    packaging: "tbd",
    // [Q: seat (automatic analysis) or plan_gated (KB adherence/CX tier)?]
    observationNames: ["tool_usage_accuracy_check"],
    traceNames: ["tool_usage_accuracy_extraction"]
  },
  {
    key: "voice_interview_agent",
    label: "Voice interview agent",
    packaging: "tbd",
    // The LiveKit interview agent conducts both recruiting interviews
    // (excluded) and training role-plays (addon) — [Q: split requires
    // per-session attribution or a packaging call].
    traceNamePrefixes: ["interview-1py-"]
  }
];
const AI_FEATURE_CATALOG = CATALOG_ENTRIES;
const USAGE_BASED_FEATURES = CATALOG_ENTRIES.filter(
  (feature) => feature.packaging === "credits"
);
const USAGE_BASED_FEATURE_KEYS = USAGE_BASED_FEATURES.map((feature) => feature.key);
function isUsageBasedFeatureKey(value) {
  return typeof value === "string" && USAGE_BASED_FEATURE_KEYS.includes(value);
}
const USAGE_BASED_FEATURE_UNIT_NOUNS = Object.fromEntries(
  USAGE_BASED_FEATURES.map((feature) => [
    feature.key,
    feature.featureUnit.noun
  ])
);
const AI_COST_PACKAGING_LABELS = {
  seat: "Seat",
  plan_gated: "Plan-gated",
  credits: "Credits",
  addon: "Add-on",
  internal: "Internal",
  tbd: "Unassigned",
  other: "Other",
  excluded: "Excluded (recruiting)"
};
const AI_COST_PACKAGING_ORDER = [
  "seat",
  "plan_gated",
  "credits",
  "addon",
  "internal",
  "tbd",
  "other",
  "excluded"
];
const featuresByKey = new Map(
  AI_FEATURE_CATALOG.map((feature) => [feature.key, feature])
);
const featuresByObservationName = /* @__PURE__ */ new Map();
const featuresByTraceName = /* @__PURE__ */ new Map();
const featuresByUnitTraceName = /* @__PURE__ */ new Map();
const featuresByUnitObservationName = /* @__PURE__ */ new Map();
const observationNamePrefixEntries = [];
const traceNamePrefixEntries = [];
const fallbackObservationNamePrefixEntries = [];
for (const feature of AI_FEATURE_CATALOG) {
  for (const name of feature.observationNames ?? []) {
    featuresByObservationName.set(name, feature);
  }
  for (const name of feature.traceNames ?? []) {
    featuresByTraceName.set(name, feature);
  }
  for (const name of feature.featureUnit?.traceNames ?? []) {
    featuresByUnitTraceName.set(name, feature);
  }
  for (const name of feature.featureUnit?.observationNames ?? []) {
    featuresByUnitObservationName.set(name, feature);
  }
  for (const prefix of feature.observationNamePrefixes ?? []) {
    observationNamePrefixEntries.push([prefix, feature]);
  }
  for (const prefix of feature.traceNamePrefixes ?? []) {
    traceNamePrefixEntries.push([prefix, feature]);
  }
  for (const prefix of feature.fallbackObservationNamePrefixes ?? []) {
    fallbackObservationNamePrefixEntries.push([prefix, feature]);
  }
}
function getAiFeatureDefinition(key) {
  return featuresByKey.get(key);
}
const AI_FEATURES_BY_UNIT_TRACE_NAME = featuresByUnitTraceName;
const AI_FEATURES_BY_UNIT_OBSERVATION_NAME = featuresByUnitObservationName;
function matchPrefix(entries, value) {
  return entries.find(([prefix]) => value.startsWith(prefix))?.[1];
}
function attributeAiFeature(observation) {
  const { name, traceName } = observation;
  const byName = featuresByObservationName.get(name) ?? matchPrefix(observationNamePrefixEntries, name);
  if (byName) {
    return byName;
  }
  if (traceName !== void 0) {
    const byTraceName = featuresByTraceName.get(traceName) ?? matchPrefix(traceNamePrefixEntries, traceName);
    if (byTraceName) {
      return byTraceName;
    }
  }
  return matchPrefix(fallbackObservationNamePrefixEntries, name);
}
export {
  AI_COST_PACKAGING_LABELS,
  AI_COST_PACKAGING_ORDER,
  AI_FEATURES_BY_UNIT_OBSERVATION_NAME,
  AI_FEATURES_BY_UNIT_TRACE_NAME,
  AI_FEATURE_CATALOG,
  AI_FEATURE_OTHER_KEY,
  USAGE_BASED_FEATURE_KEYS,
  USAGE_BASED_FEATURE_UNIT_NOUNS,
  attributeAiFeature,
  getAiFeatureDefinition,
  isUsageBasedFeatureKey
};
//# sourceMappingURL=aiFeatureCatalog.js.map
